import React from "react";

import { Col, Form, Row } from "react-bootstrap";

import { type Control, Controller, type FieldErrors } from "react-hook-form";

import { MfaPreferenceType, ParticipantStatus } from "@api/graphql";
import { FormErrorText } from "@components/form/FormErrorText";
import { useIsInternalUserCreatingAccount } from "@pages/register/hooks/useIsInternalUserCreatingAccount";
import type { UserDetailsInput } from "@pages/register/steps/RegisterFormTypes";

import styles from "./index.module.scss";

interface ContactDetailsFormProps {
  control: Control<UserDetailsInput>;
  errors: FieldErrors<UserDetailsInput>;
  handleCheckboxChange?: (checked: boolean) => void;
  isBeneficiary: boolean;
  isArtist: boolean;
  isEditing?: boolean;
  isInternalUser?: boolean;
  isCreatedUserCompletingRegistration?: boolean;
  mfaPreference?: MfaPreferenceType;
  userStatus?: string;
}

export const ContactDetailsForm: React.FC<ContactDetailsFormProps> = ({
  control,
  errors,
  handleCheckboxChange,
  isBeneficiary,
  isArtist,
  isEditing,
  isCreatedUserCompletingRegistration,
  mfaPreference,
  userStatus,
}) => {
  const isInternalUserCreatingAccount = useIsInternalUserCreatingAccount();

  return (
    <div className={styles["form-section"]}>
      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Title</Form.Label>
            <Controller
              name="contact_details.title"
              control={control}
              render={({ field }) => (
                <Form.Control {...field} as="select" className={field.value ? "custom-select-value-selected" : ""}>
                  <option hidden value="">
                    Select your title
                  </option>
                  <option value="Mr.">Mr.</option>
                  <option value="Ms.">Ms.</option>
                  <option value="Mrs.">Mrs.</option>
                  <option value="Dr.">Dr.</option>
                </Form.Control>
              )}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>* First name</Form.Label>
            <Controller
              name="contact_details.first_name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your first name"
                  isInvalid={!!errors?.contact_details?.first_name}
                />
              )}
            />
            <FormErrorText error={errors?.contact_details?.first_name} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Middle name</Form.Label>
            <Controller
              name="contact_details.middle_name"
              control={control}
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your middle name" />}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>* Last name</Form.Label>
            <Controller
              name="contact_details.last_name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your last name"
                  isInvalid={!!errors?.contact_details?.last_name}
                />
              )}
            />
            <FormErrorText error={errors?.contact_details?.last_name} />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Preferred pronoun</Form.Label>
            <Controller
              name="contact_details.pronouns"
              control={control}
              render={({ field }) => <Form.Control {...field} type="text" placeholder="Enter your preferred pronoun" />}
            />
          </Form.Group>
        </Col>
        <Col md={6} sm={12}>
          <Form.Group>
            <Form.Label>Preferred name</Form.Label>
            <Controller
              name="contact_details.preferred_name"
              control={control}
              render={({ field }) => (
                <Form.Control
                  {...field}
                  type="text"
                  placeholder="Enter your preferred name"
                  isInvalid={!!errors?.contact_details?.preferred_name}
                />
              )}
            />
            <FormErrorText error={errors?.contact_details?.preferred_name} />
          </Form.Group>
        </Col>
      </Row>

      {/* Mobile number: required for artist/beneficiary during registration; always shown when editing */}
      {(isArtist || isBeneficiary || isEditing) && (
        <Row className={styles["form-section"]}>
          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label>
                {(isArtist || isBeneficiary) &&
                (userStatus === ParticipantStatus.ACTIVE ||
                  userStatus === ParticipantStatus.PENDING_APPROVAL ||
                  !isEditing)
                  ? "* "
                  : ""}
                Mobile number
              </Form.Label>
              <Form.Text className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
                Enter a mobile phone number, including the country code for example +61412345678
              </Form.Text>
              <Controller
                name="contact_details.phone_number"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="string"
                    step={1}
                    min="0"
                    placeholder="Enter mobile number"
                    value={field.value || "+61"}
                    isInvalid={!!errors?.contact_details?.phone_number}
                    onWheel={(event) => event.currentTarget.blur()} // Prevents accidental scroll input
                  />
                )}
              />
              <FormErrorText error={errors?.contact_details?.phone_number} />
            </Form.Group>
          </Col>

          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label>Work number</Form.Label>
              <Form.Text className="text-muted d-block mb-2" style={{ fontSize: "0.75rem" }}>
                <span
                  style={{
                    visibility: "hidden",
                  }}
                >
                  Enter a mobile phone number, including the country code for example +61412345678
                </span>
              </Form.Text>
              <Controller
                name="contact_details.work_number"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="number"
                    placeholder="Enter work phone number"
                    isInvalid={!!errors?.contact_details?.work_number}
                  />
                )}
              />
              <FormErrorText error={errors?.contact_details?.work_number} />
            </Form.Group>
          </Col>

          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label>Home number</Form.Label>
              <Controller
                name="contact_details.home_number"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="number"
                    placeholder="Enter home phone number"
                    isInvalid={!!errors?.contact_details?.home_number}
                  />
                )}
              />
              <FormErrorText error={errors?.contact_details?.home_number} />
            </Form.Group>
          </Col>
          <Col md={6} sm={12}>
            <Form.Group>
              <Form.Label>
                {(isArtist || isBeneficiary) &&
                (userStatus === ParticipantStatus.ACTIVE || userStatus === ParticipantStatus.PENDING_APPROVAL)
                  ? "* "
                  : ""}
                Email
              </Form.Label>
              <Controller
                name="contact_details.email"
                control={control}
                render={({ field }) => (
                  <Form.Control
                    {...field}
                    type="email"
                    placeholder="Enter your email"
                    isInvalid={!!errors?.contact_details?.email}
                  />
                )}
              />
              <FormErrorText error={errors?.contact_details?.email} />
            </Form.Group>
          </Col>

          <>
            <Col md={6} sm={12}>
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Controller
                  name="contact_details.status"
                  control={control}
                  render={({ field }) => <Form.Control {...field} type="email" placeholder="Status" disabled />}
                />
              </Form.Group>
            </Col>
            {mfaPreference && [MfaPreferenceType.SMS_MFA, MfaPreferenceType.EMAIL_OTP].includes(mfaPreference) && (
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>MFA Preference</Form.Label>
                  <Controller
                    name="mfa_preference"
                    control={control}
                    defaultValue={mfaPreference}
                    render={({ field }) => (
                      <Form.Select {...field}>
                        <option value={MfaPreferenceType.SMS_MFA}>SMS message</option>
                        <option value={MfaPreferenceType.EMAIL_OTP}>Email message</option>
                      </Form.Select>
                    )}
                  />
                </Form.Group>
              </Col>
            )}
          </>
        </Row>
      )}

      {/* Checkbox for joining an existing account */}
      {/* Should not show if an invited user is completing their registration */}
      {!isBeneficiary && !isArtist && !isCreatedUserCompletingRegistration && !isInternalUserCreatingAccount && (
        <Row>
          <Col md={6} sm={12}>
            <Form.Group>
              <Controller
                name="join_existing_participant"
                control={control}
                defaultValue={isArtist || isBeneficiary ? false : undefined}
                render={({ field }) => (
                  <Form.Check
                    className={styles["form-check"]}
                    type="checkbox"
                    label={<Form.Label>Are you joining an existing account?</Form.Label>}
                    checked={field.value || false}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      handleCheckboxChange?.(e.target.checked);
                    }}
                    isInvalid={!!errors?.join_existing_participant}
                  />
                )}
              />
            </Form.Group>
            <FormErrorText error={errors?.join_existing_participant} />
          </Col>
        </Row>
      )}
    </div>
  );
};
